<template>
  <div class="layout reference-uniform" @scroll="handleScroll">
    <m-confirm ref="confirm" mode="danger" />
    <m-description ref="description" />
    <m-type ref="typeModal" :catalog="catalog.list" @created="onTypeCreated" @updated="onTypeUpdated" />
    <m-category ref="categoryModal" :catalog="catalog.list" @created="onCategoryCreated" @updated="onCategoryUpdated" />

    <div
      class="layout-header"
      :class="{
        'reference-uniform-header-mb-0': status === 'archive',
      }"
    >
      <div class="layout-title">Структура каталога ТМЦ</div>
      <div class="layout-header-actions">
        <div class="layout-header-action">
          <c-button
            v-if="status !== 'archive'"
            size="small"
            label="Новая категория"
            :icon="{
              icon: 'plus',
              color: 'primary',
            }"
            @click="addCategory"
          />
        </div>

        <div class="layout-header-action">
          <c-button
            v-if="status !== 'archive'"
            size="small"
            label="Новый тип ТМЦ"
            :icon="{
              icon: 'plus',
              color: 'primary',
            }"
            @click="addType"
          />
        </div>
      </div>
    </div>
    <div class="reference-uniform-options">
      <c-switcher
        v-model="status"
        class="reference-uniform-options-switcher"
        type="group"
        :items="[
          { label: 'Все', value: 'all' },
          { label: 'Активные', value: 'active' },
          { label: 'В архиве', value: 'archive' },
        ]"
      />
      <c-input
        v-model="query"
        class="reference-uniform-options-search"
        placeholder="Поиск"
        placeholder-style="italic"
        icon="search"
        type="text"
        :clearable="true"
        @enter="updateList"
      />
    </div>
    <div id="reference-uniform-list" class="reference-uniform-list">
      <template v-if="!loading">
        <template v-for="(category, categoryIndex) in catalog.list">
          <category
            :key="'category_' + category.id"
            :category="category"
            :sticked="stickyCategoryId == 'category_' + category.id"
            :last="catalog.list.length - 1 === categoryIndex"
            :root="true"
            @changeVisibility="categoryVisibleChange"
            @toggle="toggleCategory(category)"
            @showDescription="showDescription(category)"
            @edit="editCategory(category)"
            @remove="removeCategory(category)"
          />

          <b-collapse
            v-if="category.subCategories.length > 0 || category.types.length > 0"
            :key="`category_sub_${category.id}`"
            v-model="category.isVisible"
            class="reference-uniform-list-subcategories"
          >
            <template v-for="(categoryChild, categoryChildIndex) in category.subCategories">
              <sub-category
                :key="`category_sub_${categoryChild.id}_items`"
                :category="categoryChild"
                @toggle="toggleCategory(categoryChild)"
                @showDescription="showDescription(categoryChild)"
                @edit="editCategory(categoryChild)"
                @remove="removeCategory(categoryChild)"
              />

              <!-- позиции дочерней категории -->
              <b-collapse
                v-if="categoryChild.types && categoryChild.types.length > 0"
                :key="`category_sub_${categoryChild.id}_positions`"
                v-model="categoryChild.isVisible"
              >
                <type
                  v-for="(type, typeIndex) in categoryChild.types"
                  :key="'category_' + categoryIndex + '_subCategory_' + categoryChildIndex + '_position_' + typeIndex"
                  :type="type"
                  :in-subcategory="true"
                  @showDescription="showDescription(type)"
                  @edit="editType(type)"
                  @remove="removeType(type)"
                  @archiveOperation="archiveOperation(type)"
                />
              </b-collapse>
              <!-- позиции дочерней категории конец -->
            </template>

            <!-- позиции родительской категории -->
            <template v-for="(categoryChild, categoryChildIndex) in category.types">
              <type
                :key="'category_' + categoryIndex + '_position_' + categoryChildIndex"
                :type="categoryChild"
                @showDescription="showDescription(categoryChild)"
                @edit="editType(categoryChild)"
                @remove="removeType(categoryChild)"
                @archiveOperation="archiveOperation(categoryChild)"
              />
            </template>
            <!-- позиции родительской категории конец -->
          </b-collapse>
        </template>
      </template>

      <div v-if="!loading && catalog.list.length === 0" class="reference-uniform-list-empty">
        Ничего не найдено. Попробуйте изменить параметры поиска.
      </div>

      <c-loader
        v-if="loading"
        class="reference-uniform-list-loader"
        label="Загрузка данных"
        :active="loading"
        height="500"
      />
    </div>

    <transition name="fade">
      <button v-show="scY > 138" id="pagetop" class="reference-uniform-scroll-top-button" @click="toTop">
        <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5.68593 0.292893C5.2954 -0.0976311 4.66224 -0.0976311 4.27171 0.292893L0.292893 4.27191C-0.097631 4.66243 -0.0976311 5.2956 0.292893 5.68612C0.683417 6.07664 1.31658 6.07664 1.70711 5.68612L5.68593 1.70711C6.07645 1.31658 6.07645 0.683417 5.68593 0.292893Z"
            fill="#8E9CBB"
          />
          <path
            d="M4.29628 0.292893C4.6868 -0.0976311 5.31997 -0.0976311 5.71049 0.292893L9.68952 4.27191C10.08 4.66243 10.08 5.2956 9.68952 5.68612C9.299 6.07664 8.66583 6.07664 8.27531 5.68612L4.29628 1.70711C3.90575 1.31658 3.90575 0.683417 4.29628 0.292893Z"
            fill="#8E9CBB"
          />
          <path
            d="M4.97882 0.985811C4.42653 0.985811 3.97882 1.43353 3.97882 1.98581L3.97882 12.0015C3.97882 12.5537 4.42653 13.0015 4.97882 13.0015C5.5311 13.0015 5.97882 12.5537 5.97882 12.0015L5.97882 1.98581C5.97882 1.43353 5.5311 0.985811 4.97882 0.985811Z"
            fill="#8E9CBB"
          />
        </svg>
      </button>
    </transition>
  </div>
</template>
<script>
import Vue from "vue";
import { ObserveVisibility } from "vue-observe-visibility";

import Button from "@/components/controls2/Button";
import Input from "@/components/controls2/Input";
import Switcher from "@/components/controls2/Switcher";
import Loader from "@/components/loader";

import Category from "./components/Category";
import SubCategory from "./components/SubCategory";
import Type from "./components/Type";

import MConfirm from "@/components/modals/confirm-modal/";
import MDescription from "@/components/modals/description-modal";
import MType from "./modals/TypeModal";
import MCategory from "./modals/CategoryModal";

import { Catalog } from "./models/Catalog";
import {
  ARCHIVE_OPERATION_ACTION,
  GET_CATEGORIES_ACTION,
  REMOVE_CATEGORY_ACTION,
  REMOVE_TYPE_ACTION,
} from "./api";

Vue.directive("observe-visibility", ObserveVisibility);

export default {
  name: "reference-clients",

  components: {
    "c-button": Button,
    "c-input": Input,
    "c-loader": Loader,
    "c-switcher": Switcher,

    Category,
    SubCategory,
    Type,

    MConfirm,
    MDescription,
    MType,
    MCategory,
  },

  data() {
    return {
      bookmark: false,

      loading: true,
      catalog: Catalog(),

      status: "active",
      query: "",

      stickyCategoryId: "",
      categoriesInViewport: [],
      scTimer: 0,
      scY: 0,
    };
  },

  watch: {
    status() {
      this.updateList();
    },
  },

  async created() {
    this.$store.dispatch("actors/getWorkers");
    this.updateList();
  },

  methods: {
    getParams() {
      const params = {
        search: this.query,
      };
      if (this.status === "archive") params.archive = 1;
      else if (this.status === "active") params.archive = 0;
      return params;
    },
    updateList() {
      this.loading = true;
      const params = this.getParams();
      this.$store.dispatch(GET_CATEGORIES_ACTION, params).then((res) => {
        //console.lo
        this.catalog.update(res);
        this.loading = false;
      });
    },
    getCategoryChildren(categoryId) {
      const params = this.getParams();
      return this.$store
        .dispatch(GET_CATEGORY_CHILDREN_ACTION, {
          categoryId,
          ...params,
        })
        .then((result) => result[0]);
    },

    // types
    addType() {
      this.$refs.typeModal.open();
    },
    editType(type) {
      this.$refs.typeModal.open(type);
    },
    removeType(type) {
      this.$refs.confirm.open({
        title: "Удалить тип",
        text: `Тип <b>${type.name}</b> будет удален без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.catalog.removeType(type.id);
          this.$store.dispatch(REMOVE_TYPE_ACTION, type.id).then((res) => {
            if (res.success) {
              this.$notify({
                group: "success-message",
                data: {
                  textMessage: `Тип ТМЦ <b>${type.name}</b> удален`,
                },
              });
            } else {
              this.$notify({
                group: "error-message",
                data: {
                  textMessage: "Произошла ошибка. Попробуйте еще раз",
                },
              });
            }
          });
        },
      });
    },
    onTypeCreated(data) {
      const model = this.catalog.addType(data);
      this.showCategory(model.parentId);
    },
    onTypeUpdated(data) {
      const model = this.catalog.updateType(data);
      this.showCategory(model.parentId);
    },

    // description

    showDescription(item) {
      const model = item && item.descriptionModel;
      if (model.hasDescription) {
        this.$refs.description.open(model);
      }
    },

    // archive
    archiveOperation(type) {
      const action = !type.isArchive ? "to" : "from";

      this.$store
        .dispatch(ARCHIVE_OPERATION_ACTION, {
          typeId: type.id,
          action,
        })
        .then((res) => {
          if (res.success) {
            if (action === "to") {
              this.catalog.moveTypeToArchive(type.id);
              if (this.status === "active") {
                this.catalog.removeType(type.id);
              }
              this.$notify({
                group: "success-message",
                data: {
                  textMessage: `Позиция <b>${type.name}</b> успешно перемещена в архив`,
                },
              });
            } else {
              this.catalog.moveTypeFromArchive(type.id);
              if (this.status === "archive") {
                this.catalog.removeType(type.id);
              }
              this.$notify({
                group: "success-message",
                data: {
                  textMessage: `Позиция <b>${type.name}</b> успешно восстановлена из архива`,
                },
              });
            }
          }
        });
    },

    // categories
    addCategory() {
      this.$refs.categoryModal.open();
    },
    editCategory(category) {
      this.$refs.categoryModal.open(category);
    },
    onCategoryCreated(categoryData) {
      const model = this.catalog.addCategory(categoryData);
      if (model.parentId) this.showCategory(model.parentId);
    },
    onCategoryUpdated(categoryData) {
      const model = this.catalog.updateCategory(categoryData);
      this.showCategory(model.parentId);
    },
    removeCategory(category) {
      const { parentId } = category;

      this.$refs.confirm.open({
        title: parentId ? "Удалить дочернюю категорию" : "Удалить категорию",
        text: parentId
          ? `Дочерняя категория <b>${category.name}</b> будет удалена без возможности восстановления`
          : `Категория <b>${category.name}</b> будет удалена без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.$store.dispatch(REMOVE_CATEGORY_ACTION, category.id).then((res) => {
            if (res.success) {
              this.$notify({
                group: "success-message",
                data: {
                  textMessage: `Категория/подкатегория <b>${category.name}</b> удалена`,
                },
              });
              this.catalog.removeCategory(category.id);
            } else {
              this.$notify({
                group: "error-message",
                data: {
                  textMessage: "Произошла ошибка. Попробуйте еще раз",
                },
              });
            }
          });
        },
      });
    },
    hideCategory(id) {
      if (!id) return;

      this.catalog.closeCategory(id)
    },

    async showCategory(id) {
      if (!id) return;

      this.catalog.openCategory(id);
    },
    toggleCategory(category) {
      if (!category.isVisible) {
        this.showCategory(category.id);
      } else {
        this.hideCategory(category.id);
      }
    },

    // scroll
    handleArrowScrollTop() {
      if (this.scTimer) return;
      const layout = document.querySelector(".layout");
      this.scTimer = setTimeout(() => {
        this.scY = layout.scrollTop;
        clearTimeout(this.scTimer);
        this.scTimer = 0;
      }, 100);
    },
    toTop() {
      const layout = document.querySelector(".layout");
      layout.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    handleScroll() {
      if (this.catalog.list.length > 0) {
        const layout = document.querySelector(".layout");
        this.catalog.list
          .map((category) => category.id)
          .forEach((categoryId) => {
            const el = document.getElementById(`category_${categoryId}`);

            if (
              el !== null &&
              el.offsetTop <= layout.scrollTop &&
              this.categoriesInViewport.includes(`category_${categoryId}`)
            ) {
              this.stickyCategoryId = `category_${categoryId}`;
            }
          });

        const categoriesContainer = document.getElementById("reference-uniform-list");
        if (categoriesContainer.offsetTop >= layout.scrollTop) {
          this.stickyCategoryId = "";
        }
      }
      this.handleArrowScrollTop();
    },
    categoryVisibleChange([isVisible, entry]) {
      if (isVisible && !this.categoriesInViewport.includes(entry.target.id)) {
        this.categoriesInViewport.push(entry.target.id);
      }

      if (!isVisible && this.categoriesInViewport.includes(entry.target.id)) {
        this.categoriesInViewport.splice(this.categoriesInViewport.indexOf(entry.target.id), 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.reference-uniform {
  padding: 0 30px 30px 30px;
  position: relative;

  @media screen and (max-width: 1000px) {
    padding: 0 30px 30px 30px;
  }

  @media screen and (max-width: 700px) {
    padding: 0 20px 20px 20px;
  }

  &::v-deep .layout-header {
    margin-top: 30px;
    margin-bottom: 27px;

    @media screen and (max-width: 700px) {
      margin-top: 20px;
      flex-direction: column;
      align-items: flex-start;
      height: auto;

      & .layout-header-action:first-child {
        display: none !important;
      }

      & .layout-header-actions {
        margin-top: 19px;
      }
    }
  }

  &::v-deep .layout-title {
    @media screen and (max-width: 700px) {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
  }

  &-header-mb-0 {
    @media screen and (max-width: 700px) {
      margin-bottom: 0 !important;
    }
  }

  &-bookmark-mobile {
    display: none;

    @media screen and (max-width: 700px) {
      display: block;
    }
  }

  &-title {
    font-weight: 500;
    font-size: 22px;
    line-height: 108%;
  }

  &-options {
    display: flex;
    align-items: flex-end;

    @media screen and (max-width: 700px) {
      flex-direction: column;
      align-items: flex-start;
    }

    &-switcher {
      @media screen and (max-width: 700px) {
        width: 100%;
        max-width: 100%;

        &::v-deep .c-switcher-group {
          width: 100%;
          max-width: 100%;
        }

        &::v-deep .c-switcher-group-item {
          width: 33.33%;
          max-width: 33.33%;
        }
      }

      &::v-deep .c-switcher-group-item {
        background: #ffffff;
        padding: 0 22px;
      }

      &::v-deep .c-switcher-group-item-label {
        color: #8e9cbb !important;
      }

      &::v-deep .c-switcher-group-item_active {
        color: #333333 !important;
        //border: 2px solid #FF238D !important;
        position: relative;

        & .c-switcher-group-item-label {
          color: #333333 !important;
        }
      }

      &::v-deep .c-switcher-group-item.c-switcher-group-item_active:after {
        content: "";
        border: 2px solid #ff238d !important;
        position: absolute;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background: transparent;
        border: 2px solid #ff238d !important;
      }

      &::v-deep .c-switcher-group-item:first-child.c-switcher-group-item_active:after {
        content: "";
        border: 2px solid #ff238d !important;
        position: absolute;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background: transparent;
        border-radius: 15px 0 0 15px;
        border: 2px solid #ff238d !important;
      }

      &::v-deep .c-switcher-group-item:last-child.c-switcher-group-item_active:after {
        content: "";
        border: 2px solid #ff238d !important;
        position: absolute;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        background: transparent;
        border-radius: 0 15px 15px 0;
        border: 2px solid #ff238d !important;
      }
    }

    &-search {
      margin-left: 30px;
      max-width: 564px;
      width: 100%;

      @media screen and (max-width: 700px) {
        margin-left: 0;
        max-width: 100%;
        margin-top: 26px;
      }
    }
  }

  &-list {
    margin-top: 19px;
    max-width: 1190px;
    width: 100%;

    @media screen and (max-width: 700px) {
      max-width: calc(100% + 40px);
      width: calc(100% + 40px);
      margin-left: -20px;
    }

    &-subcategories {
      display: flex;
      flex-direction: column;

      & div {
        border-top: none;
      }
    }

    &-empty {
      margin-top: 34px;
      font-size: 15px;
      line-height: 80%;
      color: #8e9cbb;

      @media screen and (max-width: 700px) {
        line-height: 110%;
        margin-left: 20px;
        max-width: calc(100% - 40px);
      }
    }
  }

  &-scroll-top-button {
    position: fixed;
    width: 30px;
    height: 30px;
    box-shadow: 0px 2px 10px rgba(0, 15, 38, 0.25);
    border-radius: 50px;
    background: #ffffff;
    right: 6px;
    bottom: 7px;
    border: none;
    outline: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.cursor-pointer {
  cursor: pointer;
}
</style>

<style lang="scss">
.reference-uniform-category-empty-tooltip {
  display: flex;
  align-items: center;

  &-icon {
    margin-right: 10px;
    min-width: 14px;
  }

  &-text {
    max-width: 200px;
    font-size: 11px;
    line-height: 108%;
    color: #ff238d;
  }
}
</style>
